import { Avatar, List, Button, message, Tooltip } from 'antd';
import { _fetch } from '../../_fetch';
import moment from 'moment';
import { Comment } from '@ant-design/compatible';
import { EditOutlined } from '@ant-design/icons';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { trackEvent } from '../../utils/mixpanel';
import { useIsPrimaryClientView } from '../../utils/useIsPrimaryClientView';
import { user_role } from '../../consts';
import { ComposedInputBox, AttachedFileChip } from '../Commentshared';

// Comment-only view: add a comment (with attachments), edit your own comments.
// No "send email" / recipient-selection behavior lives here - see SendEmailView
// for that.
export default function CommentView({
    queryId,
    query_id = null,
    comments = null,
    setComments = null,
    fy = null,
    onCommentAdded = null,
    selectedPrimaryClient = false,
}) {
    const [newComment, setNewComment] = useState('');
    const { role, _id: currentUserId } = useSelector((st) => st.user);
    const { isReadOnly } = useIsPrimaryClientView();

    const [pendingFiles, setPendingFiles] = useState([]);
    const [uploadingIds, setUploadingIds] = useState({});
    const uploadUrlRef = useRef({});

    const [commentFiles, setCommentFiles] = useState({});
    const filesLoadedRef = useRef(false);

    const [editingCommentId, setEditingCommentId] = useState(null);
    const [editText, setEditText] = useState('');
    const [editPendingFiles, setEditPendingFiles] = useState([]);
    const [editUploadingIds, setEditUploadingIds] = useState({});
    const editUploadUrlRef = useRef({});

    const contextId = queryId || query_id;

    useEffect(() => {
        if (!comments?.length || filesLoadedRef.current) return;

        const fetchFiles = async () => {
            try {
                const param = contextId ? `query=${contextId}` : fy ? `fy=${fy}` : null;
                if (!param) return;

                const res = await _fetch(`${process.env.REACT_APP_API_URL}/file?${param}`);
                if (res.status === 200) {
                    const data = await res.json();
                    const filesArr = data.response || [];
                    const grouped = {};
                    filesArr.forEach(f => {
                        if (!f.commentId) return;
                        if (!grouped[f.commentId]) grouped[f.commentId] = [];
                        grouped[f.commentId].push(f);
                    });
                    setCommentFiles(grouped);
                    filesLoadedRef.current = true;
                }
            } catch (e) {
                console.error(e);
            }
        };

        fetchFiles();
    }, [comments]);

    const handleBeforeUpload = async (file) => {
        const key = `${file.name}-${Date.now()}-${Math.random()}`;
        file._key = key;

        let preview = null;
        if (file.type?.startsWith('image/')) {
            preview = URL.createObjectURL(file);
        }

        try {
            const parts = file.name.split('.');
            const ext = parts.pop();
            const baseName = parts.join('.');
            const uniqueName = `${baseName}-${moment().valueOf()}.${ext}`;

            const ctxParam = contextId ? `query=${contextId}` : fy ? `fy=${fy}` : null;
            if (!ctxParam) return false;

            const urlRes = await _fetch(
                `${process.env.REACT_APP_API_URL}/file/get_upload_url?name=${encodeURIComponent(uniqueName)}&${ctxParam}`
            );
            if (urlRes.status === 200) {
                const signedUrl = await urlRes.json();
                uploadUrlRef.current[key] = {
                    signedUrl,
                    contentType: file.type || 'application/octet-stream',
                };
            }
        } catch (e) {
            console.error('Error pre-fetching upload URL', e);
        }

        setPendingFiles(prev => [...prev, { file, preview, key, name: file.name }]);
        return false;
    };

    const removePendingFile = (key) => {
        setPendingFiles(prev => {
            const f = prev.find(p => p.key === key);
            if (f?.preview) URL.revokeObjectURL(f.preview);
            return prev.filter(p => p.key !== key);
        });
        delete uploadUrlRef.current[key];
    };

    const clearInput = () => {
        setNewComment('');
        pendingFiles.forEach(pf => pf.preview && URL.revokeObjectURL(pf.preview));
        setPendingFiles([]);
        uploadUrlRef.current = {};
    };

    const handleEditBeforeUpload = async (file) => {
        const key = `${file.name}-${Date.now()}-${Math.random()}`;
        file._key = key;

        let preview = null;
        if (file.type?.startsWith('image/')) {
            preview = URL.createObjectURL(file);
        }

        try {
            const parts = file.name.split('.');
            const ext = parts.pop();
            const baseName = parts.join('.');
            const uniqueName = `${baseName}-${moment().valueOf()}.${ext}`;

            const ctxParam = contextId ? `query=${contextId}` : fy ? `fy=${fy}` : null;
            if (!ctxParam) return false;

            const urlRes = await _fetch(
                `${process.env.REACT_APP_API_URL}/file/get_upload_url?name=${encodeURIComponent(uniqueName)}&${ctxParam}`
            );
            if (urlRes.status === 200) {
                const signedUrl = await urlRes.json();
                editUploadUrlRef.current[key] = {
                    signedUrl,
                    contentType: file.type || 'application/octet-stream',
                };
            }
        } catch (e) {
            console.error('Error pre-fetching edit upload URL', e);
        }

        setEditPendingFiles(prev => [...prev, { file, preview, key, name: file.name }]);
        return false;
    };

    const removeEditPendingFile = (key) => {
        setEditPendingFiles(prev => {
            const f = prev.find(p => p.key === key);
            if (f?.preview) URL.revokeObjectURL(f.preview);
            return prev.filter(p => p.key !== key);
        });
        delete editUploadUrlRef.current[key];
    };

    const cancelEdit = () => {
        setEditingCommentId(null);
        setEditText('');
        editPendingFiles.forEach(pf => pf.preview && URL.revokeObjectURL(pf.preview));
        setEditPendingFiles([]);
        editUploadUrlRef.current = {};
        setEditUploadingIds({});
    };

    const handleEditSubmit = async (commentId) => {
        for (const pf of editPendingFiles) {
            const { file, key } = pf;
            const meta = editUploadUrlRef.current[key];
            if (!meta) continue;

            try {
                setEditUploadingIds(prev => ({ ...prev, [key]: 10 }));

                const s3Res = await fetch(meta.signedUrl, {
                    method: 'PUT',
                    headers: { 'Content-Type': meta.contentType },
                    body: file,
                });

                setEditUploadingIds(prev => ({ ...prev, [key]: 70 }));

                if (s3Res.status === 200) {
                    const parsedUrl = new URL(meta.signedUrl);
                    const pathname = parsedUrl.pathname.slice(1);

                    const fileBody = { path: pathname, type: meta.contentType, commentId };
                    if (contextId) fileBody.query = contextId;
                    else if (fy) fileBody.fy = fy;

                    const fileRes = await _fetch(`${process.env.REACT_APP_API_URL}/file`, {
                        method: 'POST',
                        body: fileBody,
                    });

                    if (fileRes.status === 201) {
                        const savedFile = await fileRes.json();
                        setCommentFiles(prev => ({
                            ...prev,
                            [commentId]: [...(prev[commentId] || []), savedFile],
                        }));
                    }
                }

                setEditUploadingIds(prev => ({ ...prev, [key]: 100 }));
            } catch (e) {
                console.error('Edit file upload error', e);
                message.error(`Failed to upload ${file.name}`);
            } finally {
                setEditUploadingIds(prev => {
                    const n = { ...prev };
                    delete n[key];
                    return n;
                });
            }
        }

        try {
            const res = await _fetch(`${process.env.REACT_APP_API_URL}/comment/${commentId}`, {
                method: 'PATCH',
                body: { text: editText },
            });
            if (res.status === 200) {
                const updated = await res.json();
                setComments(prev =>
                    prev.map(c => c._id === commentId ? { ...c, text: updated.text } : c)
                );
                message.success('Comment updated');
            } else {
                message.error('Failed to update comment');
            }
        } catch (e) {
            console.error(e);
            message.error('Failed to update comment');
        }

        cancelEdit();
    };

    async function handleNewComment() {
        const uploadedFiles = [];
        if (pendingFiles.length > 0) {
            for (const pf of pendingFiles) {
                const { file, key } = pf;
                const meta = uploadUrlRef.current[key];
                if (!meta) continue;
                try {
                    setUploadingIds(prev => ({ ...prev, [key]: 10 }));
                    const s3Res = await fetch(meta.signedUrl, {
                        method: 'PUT',
                        headers: { 'Content-Type': meta.contentType },
                        body: file,
                    });
                    setUploadingIds(prev => ({ ...prev, [key]: 70 }));
                    if (s3Res.status === 200) {
                        const parsedUrl = new URL(meta.signedUrl);
                        const pathname = parsedUrl.pathname.slice(1);
                        uploadedFiles.push({ key, pathname, contentType: meta.contentType, name: file.name });
                    }
                    setUploadingIds(prev => ({ ...prev, [key]: 100 }));
                } catch (e) {
                    console.error('S3 upload error', e);
                    message.error(`Failed to upload ${file.name}`);
                }
            }
        }

        const payload = {
            query: queryId,
            query_id: query_id,
            text: newComment,
        };
        if (selectedPrimaryClient?._id) {
            payload.arr = [selectedPrimaryClient._id];
        }
        try {
            let res = await _fetch(`${process.env.REACT_APP_API_URL}/comment`, {
                method: 'POST',
                body: { ...payload },
            });

            if (res.status === 201) {
                let result = await res.json();
                const commentId = result._id;

                for (const uf of uploadedFiles) {
                    const fileBody = { path: uf.pathname, type: uf.contentType, commentId };
                    if (contextId) fileBody.query = contextId;
                    else if (fy) fileBody.fy = fy;

                    const fileRes = await _fetch(`${process.env.REACT_APP_API_URL}/file`, {
                        method: 'POST',
                        body: fileBody,
                    });
                    if (fileRes.status === 201) {
                        const savedFile = await fileRes.json();
                        setCommentFiles(prev => ({
                            ...prev,
                            [commentId]: [...(prev[commentId] || []), savedFile],
                        }));
                    }
                }

                message.success('Comment added successfully');
                setNewComment('');
                setComments(c => [result, ...c]);
                setPendingFiles([]);
                uploadUrlRef.current = {};
                setUploadingIds({});
                onCommentAdded && onCommentAdded({ status: 'OPEN' });

                trackEvent('Add Comment', { query_id, comment_id: commentId, query: queryId, text: newComment, fy });
                return commentId;
            }
        } catch (e) {
            console.error(e);
        }
    }

    const hasContent = newComment.trim().length > 0 || pendingFiles.length > 0;
    const isUploading = Object.keys(uploadingIds).length > 0;
    const isEditUploading = Object.keys(editUploadingIds).length > 0;

    return (
        <React.Fragment>
            <div>
                <ComposedInputBox
                    value={newComment}
                    onChange={e => setNewComment(e.target.value)}
                    pendingFiles={pendingFiles}
                    uploadingIds={uploadingIds}
                    onRemoveFile={removePendingFile}
                    onBeforeUpload={handleBeforeUpload}
                    placeholder="Type your comment..."
                />

                {hasContent && (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        marginTop: 10,
                        flexWrap: 'wrap',
                        gap: 8,
                    }}>
                        <Button onClick={clearInput}>Cancel</Button>
                        <Button
                            type="primary"
                            loading={isUploading}
                            disabled={isReadOnly}
                            onClick={async () => {
                                let commentId = await handleNewComment();
                                if (commentId) window.myFileUpload?.(commentId);
                            }}
                        >
                            Submit
                        </Button>
                    </div>
                )}
            </div>

            {comments ? (
                <List
                    className="comment-list w-100 px-3"
                    itemLayout="horizontal"
                    dataSource={comments}
                    pagination={{ pageSize: 10 }}
                    style={{ marginTop: 16 }}
                    renderItem={(item) => {
                        const filesForComment = commentFiles[item._id] || [];
                        const canEdit = item?.created_by?._id === currentUserId;
                        const isEditing = editingCommentId === item._id;

                        return (
                            <Comment
                                style={{
                                    margin: '9px 0',
                                    backgroundColor: '#FAFAFA',
                                    paddingLeft: 10,
                                    borderRadius: 8,
                                    border: '1px solid #f0f0f0',
                                }}
                                author={
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <div>
                                            <b>{item?.created_by?.first_name} {item?.created_by?.last_name}</b>
                                            <span className="ms-2" style={{ color: '#8c8c8c', fontWeight: 400, fontSize: 12 }}>
                                                {moment(item?.createdAt).format('DD MMM YYYY HH:mm')}
                                            </span>
                                        </div>

                                        {canEdit && !isEditing && (
                                            <Tooltip title="Edit comment">
                                                <Button
                                                    type="text"
                                                    size="small"
                                                    icon={<EditOutlined />}
                                                    style={{ color: '#1677ff', fontSize: 12, marginLeft: 10 }}
                                                    onClick={() => {
                                                        setEditingCommentId(item._id);
                                                        setEditText(item.text || '');
                                                        setEditPendingFiles([]);
                                                        editUploadUrlRef.current = {};
                                                    }}
                                                >
                                                    Edit
                                                </Button>
                                            </Tooltip>
                                        )}
                                    </div>
                                }
                                avatar={
                                    <Avatar shape="square" style={{ background: '#1677ff' }}>
                                        {item?.created_by?.first_name?.[0]}
                                        {item?.created_by?.last_name?.[0]}
                                    </Avatar>
                                }
                                content={
                                    <div style={{ backgroundColor: '#FAFAFA' }}>
                                        {isEditing ? (
                                            <div>
                                                <ComposedInputBox
                                                    value={editText}
                                                    onChange={e => setEditText(e.target.value)}
                                                    pendingFiles={editPendingFiles}
                                                    uploadingIds={editUploadingIds}
                                                    onRemoveFile={removeEditPendingFile}
                                                    onBeforeUpload={handleEditBeforeUpload}
                                                    placeholder="Edit comment..."
                                                />

                                                {filesForComment.length > 0 && (
                                                    <div>
                                                        <p style={{ margin: '8px 0 4px', fontSize: 11, color: '#8c8c8c' }}>
                                                            Existing attachments:
                                                        </p>
                                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                                            {filesForComment.map((f, i) => (
                                                                <AttachedFileChip key={f._id || i} file={f} />
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}

                                                <div style={{ display: 'flex', gap: 8, marginTop: 10, justifyContent: 'flex-end' }}>
                                                    <Button size="small" onClick={cancelEdit}>
                                                        Cancel
                                                    </Button>
                                                    <Button
                                                        type="primary"
                                                        size="small"
                                                        loading={isEditUploading}
                                                        disabled={!editText.trim() && editPendingFiles.length === 0}
                                                        onClick={() => handleEditSubmit(item._id)}
                                                    >
                                                        Save
                                                    </Button>
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                                <span style={{ whiteSpace: 'pre-line', fontSize: 12, fontWeight: 400 }}>
                                                    {item?.text}
                                                </span>

                                                {role !== user_role.PRIMARY_CLIENT && item?.created_for?.length > 0 && (
                                                    <p style={{ marginTop: 6, marginBottom: 4 }}>
                                                        <span style={{ color: '#6FBAFF', fontWeight: 600, fontSize: 12 }}>Recipients: </span>
                                                        <span style={{ color: '#00000073', fontWeight: 700, fontSize: 12 }}>
                                                            {item.created_for.map(el => `${el?.first_name} ${el?.last_name}`).join(', ')}
                                                        </span>
                                                    </p>
                                                )}

                                                {filesForComment.length > 0 && (
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
                                                        {filesForComment.map((f, i) => (
                                                            <AttachedFileChip key={f._id || i} file={f} />
                                                        ))}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                }
                            />
                        );
                    }}
                />
            ) : null}
        </React.Fragment>
    );
}